import { Card, Drawer, Grid, IconButton } from '@mui/material';
import { Box } from '@mui/system';
import React, { FC, useEffect, useRef, useState } from 'react';
import MediumTypography from '../../components/formlib/MediumTypography';
import { ReactComponent as Closegray } from '../../assets/images/x.svg';
import DropdownComponent from '../../components/formlib/DropdownComponent';
import Textinput from '../../components/formlib/Textinput';
import DatePickerComponent from '../../components/formlib/DatePickerComponent';
import AppTimePicker from '../../components/formlib/AppTimePicker';
import ButtonComponent from '../../components/formlib/ButtonComponent';
import { Formik, FormikProps } from 'formik';
import dayjs, { Dayjs } from 'dayjs';
import * as yup from 'yup';
import SmallTypography from '../../components/formlib/SmallTypography';
import { TimeOffHours, TimeOffListType } from './MyTimeOff';
import { LoaderContext, LoaderContextType } from '../../layouts/AppSidebar';
import { OptionType, DateString } from '../../utils/type';
import {
  datePickerMinDateMaxDateValidate,
   getFinancialYearJulyFirstDate,
  getFinancialYearJuneEndDate,
} from '../../utils/dateUtil';
import _ from 'lodash';
import ModalPopup from '../../components/formlib/ModalPopup';
import { formatDurationIntoDecimal } from '../../utils/utilities';
import { DURATION_REGEX } from '../../services/Constant';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {
  TimeOutLogCardData,
  formatTimeOffValue,
  getFieldLabels,
} from './TimeOffBalancesCard';

const isSameTimeOffName = (a?: string, b?: string): boolean => {
  if (a === b) return true;
  if (
    (a === 'Holiday' || a === 'Holiday Time') &&
    (b === 'Holiday' || b === 'Holiday Time')
  ) {
    return true;
  }
  if (
    (a === 'Flex' || a === 'Flex Time') &&
    (b === 'Flex' || b === 'Flex Time')
  ) {
    return true;
  }
  return false;
};

interface TimeOffModalProps {
  open: boolean;
  handleClose: () => void;
  selectedYear: string;
  handlesubmit: (data: TimeOffListType, action: string) => void;
  rowData?: TimeOffListType;
  availableHrs: TimeOffHours[];
  timeoffBalancelogs: TimeOutLogCardData[];
}

const initialData: TimeOffListType = {
  typeId: '',
  durationType: 'hours',
  startDate: null,
  endDate: null,
  noOfHrs: '',
  startTime: null,
  endTime: null,
  reason: '',
  leaveName: '',
};

const drawerWidth = 440;

const isWeekendDateValue = (date: Date): boolean => {
  const day = dayjs(date).day();
  return day === 0 || day === 6;
};
dayjs.extend(customParseFormat);
const parseFormikDate = (value?: DateString | Date): Dayjs | null => {
  if (!value) return null;
  if (dayjs.isDayjs(value)) return value;

  if (value instanceof Date) {
    const parsedDate = dayjs(value);
    return parsedDate.isValid() ? parsedDate : null;
  }

  if (typeof value === 'string') {
    const strictDate = dayjs(value, ['MM/DD/YYYY', 'YYYY-MM-DD'], true);
    return strictDate.isValid() ? strictDate : dayjs(value);
  }
  return null;
};
const MyTimeOffModal: FC<TimeOffModalProps> = ({
  open,
  handleClose,
  handlesubmit,
  rowData,
  selectedYear,
  availableHrs,
  timeoffBalancelogs,
}) => {
  const validationSchema = yup.object().shape({
    typeId: yup.string().required('myTimeOffTypeError'),
    durationType: yup.string(),
    reason: yup.string(),
    noOfHrs: yup
      .string()
      .required('durationRequiredMessage')
      .test(
        'range',
        'Duration must be greater than 0.00 and less than or equal to 12.00.',
        (value) => {
          if (!value) return false;
          const regex = /^(?:\d|1[0-2])\.\d{1,2}$/;
          if (!regex.test(value)) return false;
          const num = Number.parseFloat(value);
          return 0 < num && num <= 12;
        },
      )
      .nullable(),

    startDate: yup
      .date()
      .required('myTimeOffStartDateError')
      .nullable()
      .test('not-weekend', 'myTimeOffWeekendDateError', (value) => {
        if (!value) return true;
        return !isWeekendDateValue(value);
      }),

    endDate: yup
      .date()
      .required('myTimeOffEndDateError')
      .nullable()
      .test('not-weekend', 'myTimeOffWeekendDateError', (value) => {
        if (!value) return true;
        return !isWeekendDateValue(value);
      })
      .test(
        'end-after-start',
        'myTimeOffEndDateBeforeStartError',
        function (value) {
          const { effectiveStartDate } = this.parent;
          if (!effectiveStartDate || !value) return true;
          const parsedEndDate = parseFormikDate(value);
          const parsedStartDate = parseFormikDate(effectiveStartDate);
          if (!parsedEndDate || !parsedStartDate) return true;
          return !parsedEndDate.isBefore(parsedStartDate, 'day');
        },
      )
      .test(
        'end-date-beyond-fiscal-year',
        'myTimeOffDateBeyondFiscalYearError',
        (value) => {
          if (!value) return true;
          const parsedDate = parseFormikDate(value);
          if (!parsedDate) return true;
          return !parsedDate.isAfter(
            getFinancialYearJuneEndDate(selectedYear),
            'day',
          );
        },
      )
      .test('not-weekend', 'myTimeOffWeekendDateError', (value) => {
        if (!value) return true;
        return !isWeekendDateValue(value);
      }),

    startTime: yup.string().nullable().required('myTimeOffStartTimeError'),
  });
  const isWeekendDate = (date: Dayjs) => {
    const day = date.day();
    return day === 0 || day === 6;
  };

  const countWeekdays = (startDate: string, endDate: string) => {
    let count = 0;
    let currentDate = dayjs(startDate, 'MM/DD/YYYY');
    const lastDate = dayjs(endDate, 'MM/DD/YYYY');

    while (!currentDate.isAfter(lastDate, 'day')) {
      if (!isWeekendDate(currentDate)) {
        count += 1;
      }
      currentDate = currentDate.add(1, 'day');
    }

    return count;
  };
  const [timeOff, setTimeOff] = useState<TimeOffListType>();
  const [displayTimeoffLog, setDisplayTimeoffLog] =
    useState<TimeOutLogCardData | null>(null);
  const [action, setAction] = useState<'add' | 'edit'>('add');
  const [openModal, setOpenModal] = React.useState(false);
  const formRef = useRef<FormikProps<TimeOffListType>>(null);
  const { loader, toggleLoader } = React.useContext(
    LoaderContext,
  ) as LoaderContextType;
  const leaveTypeData: OptionType[] = availableHrs
    .map((data) => ({
      id: data.id,
      label: data.type,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const validateTimeOff = (values: TimeOffListType) => {
    const errors: Partial<TimeOffListType> = {};

    if (values.startDate) {
      const minFiscalYearCurrentMonth =
        getFinancialYearJulyFirstDate(selectedYear);
      const maxFiscalYearCurrentMonth =
        getFinancialYearJuneEndDate(selectedYear);
      if (
        dayjs(values.startDate).isBefore(minFiscalYearCurrentMonth) ||
        dayjs(values.startDate).isAfter(maxFiscalYearCurrentMonth)
      ) {
        errors.startDate = 'BlockTime.startDateValidationError';
      }
    }

    if (datePickerMinDateMaxDateValidate(values.startDate)) {
      errors.startDate = 'datePickerMinDateMaxDateValidate';
    }

    if (values.endDate) {
      if (datePickerMinDateMaxDateValidate(values.endDate)) {
        errors.endDate = 'datePickerMinDateMaxDateValidate';
      }
      if (
        values.startDate &&
        dayjs(values.endDate).isBefore(dayjs(values.startDate))
      ) {
        errors.endDate = 'myTimeOffEndDateBeforeStartError';
      }
    }

    return errors;
  };

  useEffect(() => {
    if (rowData) {
      const value = rowData.startTime
        ? `${dayjs().format('YYYY-MM-DD')}T${rowData.startTime}`
        : null;

      setTimeOff({
        id: rowData.id,
        typeId: rowData.typeId,
        noOfHrs: formatDurationIntoDecimal(rowData.noOfHrs),
        durationType: rowData.durationType,
        startDate: rowData.startDate,
        endDate: rowData.endDate,
        startTime: value,
        endTime: rowData.endTime,
        reason: rowData.reason,
        leaveName: rowData.leaveName,
      });
      //}
      const selectedTimeOffBalance = timeoffBalancelogs.find((item) =>
        isSameTimeOffName(item.name, rowData?.leaveName),
      );
      setDisplayTimeoffLog(selectedTimeOffBalance ?? null);
      setAction('edit');
    } else {
      setTimeOff(initialData);
      setAction('add');
    }
  }, []);
  useEffect(() => {
    if (!timeOff?.typeId) {
      setDisplayTimeoffLog(null);
      return;
    }

    const selectedType = availableHrs.find(
      (item) => String(item.id) === String(timeOff.typeId),
    );

    if (!selectedType) {
      setDisplayTimeoffLog(null);
      return;
    }
    const selectedBalance = timeoffBalancelogs.find((item) =>
      isSameTimeOffName(item.name, selectedType.type),
    );

    if (selectedBalance) {
      setDisplayTimeoffLog(selectedBalance);
    }else{
      setDisplayTimeoffLog(null);
    }
    
  }, [timeOff?.typeId, availableHrs, timeoffBalancelogs]);

  useEffect(() => {
    if (timeOff && formRef?.current) {
      formRef.current?.setValues({
        ...timeOff,
      });
    }
  }, [timeOff?.durationType]);

  const clearForm = () => {
    formRef.current?.setValues(formRef.current?.initialValues);
    setTimeOff(formRef.current?.initialValues);
    setOpenModal(false);
  };
  
  return (
    <Drawer
      sx={{
        width: drawerWidth,
        paddingInline: '24px',
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
        },
      }}
      anchor={'right'}
      open={open}
      onClose={handleClose}
    >
      <ModalPopup
        open={openModal}
        description="formUnsavedChangesMessage"
        onCancel={() => setOpenModal(false)}
        onOk={() => clearForm()}
        labelId1="Clientpage.Nobtn"
        negativeActionLabel="No"
        labelId2="Clientpage.Yesbtn"
        positiveActionLabel="Yes"
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: '#E7F0F0',
          padding: '0px 0px 0px 0px !important',
        }}
      >
        <Card
          sx={{
            my: '0px',
            ml: '0px',
            mt: '0px',
            padding: '24px',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {timeOff && (
            <Formik
              initialValues={timeOff}
              innerRef={formRef}
              onSubmit={(values: TimeOffListType) => {
                toggleLoader(true);
                handlesubmit(values, action);
              }}
              validationSchema={validationSchema}
              validate={validateTimeOff}
            >
              {({
                values,
                setFieldValue,
                handleSubmit,
                initialValues,
                errors,
                touched,
              }) => {
                const selectedDays =
                  values.startDate && values.endDate
                    ? countWeekdays(values.startDate, values.endDate)
                    : null;
                const durationHours = Number(values.noOfHrs);
                const totalHours =
                  selectedDays !== null && !Number.isNaN(durationHours)
                    ? Number((selectedDays * durationHours).toFixed(3))
                    : null;

                return (
                  <Grid container>
                    <Grid container direction="row" alignItems="center">
                      <Grid item xs={10.7}>
                        <MediumTypography
                          sxProps={{
                            color: '#2A4241',
                            fontSize: '16px',
                            fontWeight: '600',
                            lineHeight: '36px',
                            fontFamily: 'Lato-Bold',
                          }}
                          labelid={'myTimeOffRequestTimeOff'}
                          defaultlabel={'Request Time Off'}
                        />
                      </Grid>
                      <Grid item xs={1.3} textAlign={'right'}>
                        <IconButton onClick={handleClose}>
                          <Closegray />
                        </IconButton>
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      alignItems="center"
                      sx={{ padding: '16px 0px 16px 0px' }}
                    >
                      <Grid item xs={12}>
                        <DropdownComponent
                          names={leaveTypeData}
                          labelid="MyTimeOffSelectType"
                          defaultlabelid="Select Type*"
                          value={values.typeId}
                          handleChange={(value) => {
                            setFieldValue('typeId', value);
                            setTimeOff({
                              ...timeOff,
                              typeId: value,
                            });
                          }}
                        />
                        {errors.typeId && touched.typeId && (
                          <SmallTypography
                            sxProps={{ color: 'red' }}
                            labelId={errors.typeId}
                          />
                        )}
                      </Grid>
                    </Grid>

                    <Grid
                      container
                      direction="row"
                      columnSpacing={1}
                      alignItems="center"
                    >
                      <Grid item xs={6}>
                        <MediumTypography
                          sxProps={{
                            color: '#2A4241',
                            fontWeight: '600',
                            lineHeight: '18px',
                            fontFamily: 'Lato-Bold',
                          }}
                          labelid="myTimeOffStartDate"
                          defaultlabel="Start Date*"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <MediumTypography
                          sxProps={{
                            color: '#2A4241',
                            fontWeight: '600',
                            lineHeight: '18px',
                            fontFamily: 'Lato-Bold',
                          }}
                          labelid="myTimeOffEndDate"
                          defaultlabel="End Date*"
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      direction="row"
                      columnSpacing={1}
                      alignItems="center"
                      marginTop={2}
                    >
                      <Grid item xs={6}>
                        <DatePickerComponent
                          labelid="myTimeOffStartDate"
                          disableFuture={false}
                          defaultlabelid="Start Date*"
                          minDate={getFinancialYearJulyFirstDate(selectedYear)}
                          maxDate={getFinancialYearJuneEndDate(selectedYear)}
                          shouldDisableDate={isWeekendDate}
                          value={
                            values.startDate === '' || values.startDate === null
                              ? null
                              : dayjs(values.startDate)
                          }
                          handlechange={(value) => {
                            const formattedDate =
                              dayjs(value).format('MM/DD/YYYY');
                            if (value === null) {
                              setFieldValue('startDate', null);
                              setTimeOff({
                                ...timeOff,
                                startDate: null,
                              });
                            } else {
                              setFieldValue('startDate', formattedDate);
                              setTimeOff({
                                ...timeOff,
                                startDate: formattedDate,
                              });
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <DatePickerComponent
                          labelid="myTimeOffEndDate"
                          disableFuture={false}
                          defaultlabelid="End Date"
                          minDate={getFinancialYearJulyFirstDate(selectedYear)}
                          maxDate={getFinancialYearJuneEndDate(selectedYear)}
                          shouldDisableDate={isWeekendDate}
                          value={
                            values.endDate === '' || values.endDate === null
                              ? null
                              : dayjs(values.endDate)
                          }
                          handlechange={(value) => {
                            const formattedDate =
                              dayjs(value).format('MM/DD/YYYY');
                            if (value === null) {
                              setFieldValue('endDate', null);
                              setTimeOff({
                                ...timeOff,
                                endDate: null,
                              });
                            } else {
                              setFieldValue('endDate', formattedDate);
                              setTimeOff({
                                ...timeOff,
                                endDate: formattedDate,
                              });
                            }
                          }}
                        />
                      </Grid>
                    </Grid>
                    <Grid container direction="row">
                      <Grid item xs={6}>
                        {errors.startDate && touched.startDate && (
                          <SmallTypography
                            sxProps={{ color: 'red' }}
                            labelId={errors.startDate}
                          />
                        )}
                      </Grid>
                      <Grid item xs={6}>
                        {errors.endDate && touched.endDate && (
                          <SmallTypography
                            sxProps={{ color: 'red' }}
                            labelId={errors.endDate}
                          />
                        )}
                      </Grid>
                    </Grid>
                    {selectedDays !== null && (
                      <Grid container direction="row" marginTop={1}>
                        <Grid item xs={12}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                          >
                            <MediumTypography label="Number of days selected:" />
                            <MediumTypography
                              label={`${selectedDays}`}
                              sxProps={{ color: 'var(--thom-green-primary)' }}
                            />
                          </Box>
                        </Grid>
                      </Grid>
                    )}

                    <Grid
                      container
                      direction="row"
                      columnSpacing={1}
                      alignItems="center"
                      marginTop={2}
                    >
                      <Grid item xs={6}>
                        <MediumTypography
                          sxProps={{
                            color: '#2A4241',
                            fontWeight: '600',
                            fontFamily: 'Lato-Bold',
                          }}
                          labelid="BlockTime.startTime"
                          defaultlabel=" Start Time* "
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <MediumTypography
                          sxProps={{
                            color: '#2A4241',
                            fontWeight: '600',
                            fontFamily: 'Lato-Bold',
                          }}
                          labelid="BlockTime.noOfHours"
                          defaultlabel=" No. of Hours Per Day*"
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      direction="row"
                      columnSpacing={1}
                      alignItems="center"
                      marginTop={2}
                    >
                      <Grid item xs={6}>
                        <AppTimePicker
                          minuteStep={15}
                          value={
                            values.startTime === null
                              ? null
                              : dayjs(timeOff.startTime)
                          }
                          onTimeChange={(text) => {
                            if (text === null) {
                              setFieldValue('startTime', null);
                              setTimeOff({
                                ...timeOff,
                                startTime: null,
                              });
                            } else {
                              setFieldValue(
                                'startTime',
                                `${text.format('HH:mm')}`,
                              );
                              setTimeOff({
                                ...timeOff,
                                startTime: `${dayjs().format(
                                  'YYYY-MM-DD',
                                )}T${text.format('HH:mm')}`,
                              });
                            }
                          }}
                          sxButtonProps={{
                            padding: '16px 40px 16px 8px',
                            width: '100%',
                          }}
                          name="Start Time*"
                        />
                      </Grid>

                      <Grid item xs={6}>
                        <Textinput
                          name="noOfHrs"
                          Value={values.noOfHrs}
                          labelid="myDuration"
                          defaultlabelid="No. of Hours Per Day"
                          inputProps={{
                            maxLength: 5,
                          }}
                          handlechange={(value) => {
                            if (DURATION_REGEX.test(value)) {
                              setFieldValue('noOfHrs', value);
                              setTimeOff({ ...timeOff, noOfHrs: value });
                            }
                          }}
                          Required={true}
                          onblur={() => {
                            const formatted = formatDurationIntoDecimal(
                              values.noOfHrs,
                            );
                            setFieldValue('noOfHrs', formatted);
                            setTimeOff({ ...timeOff, noOfHrs: formatted });
                          }}
                        />
                      </Grid>
                    </Grid>
                    <Grid container xs={12} direction="row">
                      <Grid item xs={5}>
                        {errors.startTime && touched.startTime && (
                          <SmallTypography
                            sxProps={{ color: 'red' }}
                            labelId={errors.startTime}
                          />
                        )}
                      </Grid>
                      <Grid item xs={1}></Grid>
                      <Grid item xs={5}>
                        {errors.noOfHrs && touched.noOfHrs && (
                          <SmallTypography
                            sxProps={{ color: 'red' }}
                            labelId={errors.noOfHrs}
                          />
                        )}
                      </Grid>
                    </Grid>
                    {totalHours !== null && (
                      <Grid container direction="row" marginTop={1}>
                        <Grid item xs={12}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                          >
                            <MediumTypography label="Total Hours:" />
                            <MediumTypography
                              label={`${totalHours}`}
                              sxProps={{ color: 'var(--thom-green-primary)' }}
                            />
                          </Box>
                        </Grid>
                      </Grid>
                    )}

                    <Grid
                      container
                      alignItems="center"
                      sx={{ padding: '16px 0px 24px 0px' }}
                    >
                      <Grid item xs={12}>
                        <Textinput
                          name="reason"
                          Value={values.reason}
                          labelid="myTimeOffEnterReason"
                          defaultlabelid="Enter Reason"
                          inputProps={{
                            maxLength: 50,
                          }}
                          handlechange={(value) => {
                            setFieldValue('reason', value);
                            setTimeOff({ ...timeOff, reason: value });
                          }}
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      direction="row"
                      alignItems="right"
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        paddingBottom: '50px',
                      }}
                    >
                      <Grid item sx={{ marginRight: '16px' }}>
                        <ButtonComponent
                          className="btn-primary btn-cancel"
                          variantType="contained"
                          labelId="myTimeOffCancelButtonText"
                          defaultLabelId="Cancel"
                          onClick={() => {
                            if (_.isEqual(initialValues, values)) {
                              handleClose();
                            } else {
                              setOpenModal(true);
                            }
                          }}
                        />
                      </Grid>
                      <Grid item>
                        <ButtonComponent
                          className="btn-primary btn-submit"
                          variantType="contained"
                          type="submit"
                          loading={loader}
                          labelId={
                            action === 'add'
                              ? 'myTimeOffSubmitButtonText'
                              : 'myTimeOffUpdateButtonText'
                          }
                          defaultLabelId={
                            action === 'add' ? 'Submit' : 'Update'
                          }
                          onClick={() => {
                            handleSubmit();
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                );
              }}
            </Formik>
          )}
          {/* ================= TIME OFF BALANCE ================= */}
          {displayTimeoffLog && (
            <Box
              sx={{
                width: '100%',
                padding: '12px 16px',
                marginTop: 'auto',
                boxSizing: 'border-box',
              }}
            >
              <Grid container spacing={0}>
                {getFieldLabels(displayTimeoffLog.name).map(({ key, label }) => {
                  const value = displayTimeoffLog[key];
                  if (value == null || value === '') return null;
                  return (
                    <Grid item xs={12} key={key}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginBottom: '6px',
                        }}
                      >
                        <MediumTypography
                          label={label}
                          sxProps={{
                            fontSize: '10px',
                            fontWeight: 1000,
                            color: '#2A4241',
                          }}
                        />
                        <MediumTypography
                          label={`${formatTimeOffValue(value)} hrs`}
                          sxProps={{
                            fontSize: '10px',
                            fontWeight: 700,
                            color: '#00AFA5',
                          }}
                        />
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}
        </Card>
      </Box>
    </Drawer>
  );
};
export default MyTimeOffModal;
